import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { Button } from '../Common/button';
import {
  PROJECT_DASHBOARD_WIDGETS,
  isWidgetHiddenByExperience,
  moveItem,
} from '../../lib/profileUi';
import {
  DASHBOARD_WIDGETS,
  type ControlledResource,
  type DashboardWidgetId,
  type ProfileExperience,
} from '../../types/profiles';

export interface DashboardWidgetChoice {
  id: DashboardWidgetId;
  visible: boolean;
}

interface ProfileDashboardEditorProps {
  /** true → the default dashboard (ui_config.dashboard = null). */
  useDefault: boolean;
  onUseDefaultChange: (value: boolean) => void;
  /** Full catalog in display order. */
  widgets: DashboardWidgetChoice[];
  onWidgetsChange: (next: DashboardWidgetChoice[]) => void;
  /** Read grant of the permissions being edited. */
  canRead: (resource: ControlledResource) => boolean;
  /** Experience chosen on the Navigation tab ('' = inherit, unknown here). */
  experience: ProfileExperience | '';
  readOnly?: boolean;
}

/** Dashboard tab of the profile editor: widget catalog (plan §9.5). */
export const ProfileDashboardEditor: React.FC<ProfileDashboardEditorProps> = ({
  useDefault,
  onUseDefaultChange,
  widgets,
  onWidgetsChange,
  canRead,
  experience,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const listDisabled = readOnly || useDefault;

  const toggle = (id: DashboardWidgetId, visible: boolean) => {
    onWidgetsChange(widgets.map((w) => (w.id === id ? { ...w, visible } : w)));
  };

  return (
    <div className="space-y-4">
      <label className="flex items-start gap-2 text-sm" htmlFor="profile-dashboard-default">
        <input
          id="profile-dashboard-default"
          type="checkbox"
          className="h-4 w-4 mt-0.5 cursor-pointer disabled:cursor-not-allowed"
          checked={useDefault}
          disabled={readOnly}
          onChange={(e) => onUseDefaultChange(e.target.checked)}
        />
        <span>
          <span className="font-medium block">
            {t('profiles.dashboard.useDefault', 'Use the default dashboard')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t(
              'profiles.dashboard.useDefaultHelp',
              'Members see the standard dashboard. Uncheck to choose and order the widgets.'
            )}
          </span>
        </span>
      </label>

      <p className="text-xs text-muted-foreground">
        {t(
          'profiles.dashboard.permissionNote',
          'A widget only appears for members whose profile can read its data.'
        )}
      </p>

      <ul
        className={`border border-border rounded-lg divide-y divide-border/60 ${useDefault ? 'opacity-60' : ''}`}
        aria-disabled={listDisabled}
      >
        {widgets.map((w, index) => {
          const resource = DASHBOARD_WIDGETS[w.id];
          const readable = canRead(resource);
          const isProjectWidget = PROJECT_DASHBOARD_WIDGETS.includes(w.id);
          const experienceHidden =
            experience !== '' && isWidgetHiddenByExperience(w.id, experience);
          const label = t(`profiles.dashboard.widgets.${w.id}`);
          const checkboxId = `dashboard-widget-${w.id}`;
          return (
            <li key={w.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <input
                id={checkboxId}
                type="checkbox"
                className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                checked={w.visible && readable}
                disabled={listDisabled || !readable}
                onChange={(e) => toggle(w.id, e.target.checked)}
              />
              <label htmlFor={checkboxId} className="flex-1 min-w-0">
                <span className={readable ? 'font-medium' : 'font-medium text-muted-foreground'}>
                  {label}
                </span>
                {isProjectWidget && (
                  <span className="ml-2 inline-block text-xs bg-muted px-2 py-0.5 rounded">
                    {t('profiles.dashboard.projectsModule', 'Available with the Projects module')}
                  </span>
                )}
                {!readable && (
                  <span className="block text-xs text-muted-foreground">
                    {t('profiles.navigation.noRead', 'Requires read permission on {{resource}}', { resource })}
                  </span>
                )}
                {readable && experienceHidden && (
                  <span className="block text-xs text-muted-foreground">
                    {t('profiles.navigation.hiddenByExperience', 'Hidden by the Olisé experience')}
                  </span>
                )}
              </label>
              <div className="flex">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  disabled={listDisabled || index === 0}
                  onClick={() => onWidgetsChange(moveItem(widgets, index, -1))}
                  aria-label={t('profiles.navigation.moveUp', 'Move up {{item}}', { item: label })}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  disabled={listDisabled || index === widgets.length - 1}
                  onClick={() => onWidgetsChange(moveItem(widgets, index, 1))}
                  aria-label={t('profiles.navigation.moveDown', 'Move down {{item}}', { item: label })}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
